<template>
  <div
    :class="[
      suit(),
      !state.canRefine && suit('','noRefinement')
    ]"
    v-if="state"
  >
    <slot
      :items="items"
      :refine="refine"
      :search-for-items="state.searchForItems"
      :search-for-items-query="searchForFacetValuesQuery"
      :toggle-show-more="toggleShowMore"
      :can-toggle-show-more="state.canToggleShowMore"
      :is-showing-more="state.isShowingMore"
      :createURL="state.createURL"
      :is-from-search="state.isFromSearch"
      :can-refine="state.canRefine"
      :send-event="state.sendEvent"
    >
      <div
        :class="`${suit('searchBox')} relative mb-2`"
        v-if="searchable"
      >
        <div class="pointer-events-none absolute inset-y-0 left-3 flex items-center pl-3">
          <div class="text-gray-500 sm:text-sm">
            <i class="fa-solid fa-magnifying-glass"></i>
          </div>
        </div>
        <search-input
          v-model="searchForFacetValues"
          :placeholder="searchablePlaceholder"
          :class-names="classNames"
        />
      </div>
      <slot
        name="noResults"
        :query="searchForFacetValues"
        v-if="items.length === 0"
      >
        <div :class="suit('noResults')"><p>No options.</p></div>
      </slot>
      <ul :class="suit('list')">
        <li
          :class="[
          suit('item'), item.isRefined && suit('item', 'selected'), 'form-control', 'pb-4']"
          v-for="item in items"
          :key="item.value"
        >
          <slot
            name="item"
            :item="item"
            :refine="refine"
            :createURL="state.createURL"
          >
            <div :class="[suit('itemContainer'), 'inline-flex', 'items-center', 'justify-between', 'gap-4', 'lg:justify-end', 'lg:flex-row-reverse']">
              <label :for="`${attribute}-${item.value.replace(' ', '_')}`" :class="[suit('labelText'), 'mt-px', 'font-normal', 'text-light-blue-2', 'cursor-pointer', 'select-none', 'leading-4', 'w-full']">
                <span
                  class="label-text text-dark-blue-2 font-medium text-base"
                >
                  <ais-highlight
                    attribute="item"
                    :hit="item"
                    v-if="searchable"
                  />
                  <template v-else>{{ item.label }}</template>
                  <span :class="[suit('count'), 'ml-2', 'bg-light-grey-2', 'px-2', 'rounded-full', 'text-xs', 'text-light-blue-2', 'py-[1px]']">{{ item.count }}</span>
                </span>
              </label>
              <label :class="[suit('label'), 'relative', 'flex', 'items-center', 'rounded-full', 'cursor-pointer']">
                <input
                  :class="[suit('checkbox'), 'peer', 'relative', 'h-6', 'w-6', 'cursor-pointer', 'appearance-none', 'border', 'border-dark-blue-2', 'transition-all', 'min-w-[24px]', 'rounded-[4px]', 'checked:bg-dark-blue-2', 'focus:shadow-none', 'focus:border-0']"
                  type="checkbox"
                  :id="`${attribute}-${item.value.replace(' ', '_')}`"
                  :value="item.value"
                  :checked="item.isRefined"
                  @change="refine(item.value)"
                >
                <span :class="[suit('checkboxFake'), 'absolute', 'text-white', 'transition-opacity', 'opacity-0', 'pointer-events-none', 'top-[7px]', 'left-[6px]', 'peer-checked:opacity-100']">
                  <svg width="13" height="10" viewBox="0 0 13 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1.5 5L5 8.5L12 1.5" stroke="white" stroke-width="2"></path>
                  </svg>
                </span>
              </label>
            </div>
          </slot>
        </li>
      </ul>
      <slot
        name="showMore"
        :is-showing-more="state.isShowingMore"
        :toggle-show-more="toggleShowMore"
        :can-toggle-show-more="state.canToggleShowMore"
      >
        <button
          :class="[
            suit('showMore'),
            {
              [suit('showMore', 'disabled')]: !state.canToggleShowMore,
            },
            'text-sm',
            'font-bold',
            'leading-4',
            'underline',
            'text-dark-blue-2',
            'mt-2',
            '',
          ]"
          @click="toggleShowMore"
          v-if="showMore"
          v-show="state.canToggleShowMore"
        >
          <slot
            name="showMoreLabel"
            :is-showing-more="state.isShowingMore"
          >Show {{ state.isShowingMore ? 'less' : 'more' }}</slot>
        </button>
      </slot>
    </slot>
  </div>
</template>

<script>
import { createWidgetMixin, createSuitMixin, AisHighlight } from 'vue-instantsearch/vue3/es';
import { createPanelConsumerMixin } from 'vue-instantsearch/src/mixins/panel';
import { connectRefinementList } from 'instantsearch.js/es/connectors';
import SearchInput from './SearchInput.vue';

const noop = () => {};

export default {
  name: 'AisRefinementList',
  components: { SearchInput, AisHighlight },
  mixins: [
    createSuitMixin({ name: 'RefinementList' }),
    createWidgetMixin({ connector: connectRefinementList }),
    createPanelConsumerMixin({
      mapStateToCanRefine: state => Boolean(state.canRefine),
    }),
  ],
  props: {
    attribute: {
      type: String,
      required: true,
    },
    searchable: {
      type: Boolean,
      default: undefined,
    },
    searchablePlaceholder: {
      type: String,
      required: false,
      default: 'Search here…',
    },
    operator: {
      default: 'or',
      validator(value) {
        return value === 'and' || value === 'or';
      },
      required: false,
    },
    limit: {
      type: Number,
      required: false,
      default: undefined,
    },
    showMoreLimit: {
      type: Number,
      required: false,
      default: undefined,
    },
    showMore: {
      type: Boolean,
      required: false,
      default: false,
    },
    sortBy: {
      type: [Array, Function],
      required: false,
      default: undefined,
    },
    transformItems: {
      type: Function,
      required: false,
      default: undefined,
    },
  },
  data() {
    return {
      searchForFacetValuesQuery: '',
    };
  },
  computed: {
    searchForFacetValues: {
      get() {
        return this.searchForFacetValuesQuery;
      },
      set(value) {
        this.state.searchForItems(value);
        this.searchForFacetValuesQuery = value;
      },
    },
    toggleShowMore() {
      return this.state.toggleShowMore || noop;
    },
    items() {
      return this.state.items.map(item =>
        Object.assign({}, item, {
          _highlightResult: {
            item: {
              value: item.highlighted,
            },
          },
        })
      );
    },
    widgetParams() {
      return {
        attribute: this.attribute,
        operator: this.operator,
        limit: this.limit,
        showMore: this.showMore,
        showMoreLimit: this.showMoreLimit,
        sortBy: this.sortBy,
        escapeFacetValues: true,
        transformItems: this.transformItems,
      };
    },
  },
  methods: {
    refine(value) {
      this.state.refine(value);
      this.searchForFacetValuesQuery = '';
    },
  },
};
</script>
